import type { FC, PropsWithChildren } from 'react'
import { useState } from 'react'
import { AnimatePresence, type HTMLMotionProps, motion } from 'motion/react'
import { Box, type BoxProps, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { NativeModal } from '@dhl/native-modal'
import { Button } from '@dhl-ui/components'
import { AgreedPlaceForm, type AgreedPlaceFormValues } from './AgreedPlaceForm.js'
import { NotHomeForm } from './NotHomeForm.js'
import type {
  AgreedPlaceInterventionPreferenceWithPhoto,
  PreferenceFormValues,
} from '~/profile/model/preferences/model.js'

type SlideVariants = 'hidden' | 'visible'

type SlideProps = HTMLMotionProps<'div'> & {
  height?: string
  initial?: boolean | SlideVariants
  innerProps?: BoxProps
  visible?: boolean
  variants: Record<SlideVariants, { x: string | number }>
}

const Slide = ({
  children,
  height = 'inherit',
  initial,
  innerProps,
  variants,
  visible = true,
  ...rest
}: PropsWithChildren<SlideProps>) => (
  <motion.div
    {...rest}
    animate={visible ? 'visible' : 'hidden'}
    initial={initial}
    style={{ height, minWidth: '100%' }}
    transition={{
      x: { type: 'tween', duration: 0.3, delay: 0 },
      opacity: { duration: 0.3 },
    }}
    variants={{
      visible: { x: variants.visible.x, opacity: 1 },
      hidden: { x: variants.hidden.x, opacity: 0 },
    }}
  >
    <Box height="inherit" overflow="auto" {...innerProps}>
      {children}
    </Box>
  </motion.div>
)

type Props = {
  agreedPlace: AgreedPlaceInterventionPreferenceWithPhoto | null
  open: boolean
  onClose?: () => void
  isAgreedPlacePreferenceAvailable: boolean
  onChange: () => void
  onOpen: () => void
  buttonText?: string
}

export const NotHomeDialog: FC<Props> = ({
  open,
  onClose,
  agreedPlace,
  isAgreedPlacePreferenceAvailable,
  onChange,
  onOpen,
  buttonText,
}) => {
  const { t } = useTranslation()
  const [showAgreedPlaceForm, setShowAgreedPlaceForm] = useState<boolean>(false)
  const [previousOpen, setPreviousOpen] = useState(open)

  if (open !== previousOpen) {
    setPreviousOpen(open)
    if (!open) {
      setShowAgreedPlaceForm(false)
    }
  }
  const { setFieldValue } = useFormikContext<PreferenceFormValues>()

  const handleBackButtonClick = () => {
    if (showAgreedPlaceForm) {
      setShowAgreedPlaceForm(false)
    } else {
      onClose?.()
    }
  }

  const handleChange = () => {
    onClose?.()
    onChange()
  }

  const handleOnConfigureAgreedPlaceClick = () => setShowAgreedPlaceForm(true)

  /**
   * The agreedPlace is the only value what we want to know for the not home preference in the first phase.
   * When neigbours has been selected, then the agreedPlace value will be deactivated.
   */
  const handleNotHomeChange = async (isAgreedPlaceEnabled: boolean) => {
    await setFieldValue('agreedPlace', {
      ...agreedPlace,
      description: agreedPlace?.description ?? '',
      ringDoorbell: isAgreedPlaceEnabled,
      active: isAgreedPlaceEnabled,
    })
    handleChange()
  }

  const handleAgreedPlaceChange = async (values: AgreedPlaceFormValues) => {
    await setFieldValue('agreedPlace', { ...agreedPlace, ...values, ringDoorbell: true, active: true })
    setShowAgreedPlaceForm(false)
    handleChange()
  }

  return (
    <NativeModal data-testid="not-home-dialog" open={open} onClose={onClose} onOpen={onOpen}>
      <NativeModal.Header
        title={
          showAgreedPlaceForm
            ? t('portal.preferences.agreedPlace.dialogTitleText')
            : t('portal.preferences.notHome.dialogTitleText')
        }
        onBack={showAgreedPlaceForm ? handleBackButtonClick : undefined}
        onClose={onClose}
      />
      <NativeModal.Content>
        <Stack flexDirection="row" sx={{ overflowX: 'hidden' }}>
          <AnimatePresence initial={false}>
            <Slide
              key="not-home-form"
              visible={!showAgreedPlaceForm}
              variants={{
                hidden: { x: '-120%' },
                visible: { x: '0%' },
              }}
            >
              <NotHomeForm
                agreedPlace={agreedPlace}
                formId="not-home-form"
                isAgreedPlacePreferenceAvailable={isAgreedPlacePreferenceAvailable}
                onChange={handleNotHomeChange}
                onConfigureAgreedPlaceClick={handleOnConfigureAgreedPlaceClick}
              />
            </Slide>
            <Slide
              initial="hidden"
              key="not-home-agreedplace"
              visible={showAgreedPlaceForm}
              variants={{
                hidden: { x: '20%' },
                visible: { x: '-100%' },
              }}
            >
              <Box>
                <AgreedPlaceForm
                  agreedPlace={agreedPlace}
                  formId="agreed-place-form"
                  onChange={handleAgreedPlaceChange}
                />
              </Box>
            </Slide>
          </AnimatePresence>
        </Stack>
      </NativeModal.Content>
      <NativeModal.Footer>
        <>
          {showAgreedPlaceForm && (
            <Button
              fullWidth
              data-testid="submit-description"
              form="agreed-place-form"
              variant="contained"
              type="submit"
            >
              {buttonText ?? t('portal.preferences.agreedPlace.saveButtonText')}
            </Button>
          )}

          {!showAgreedPlaceForm && (
            <Button fullWidth type="submit" form="not-home-form" variant="contained" data-testid="submit-not-home">
              {buttonText ?? t('portal.preferences.notHome.saveButton')}
            </Button>
          )}
        </>
      </NativeModal.Footer>
    </NativeModal>
  )
}
